import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

/** Valor del día de corte que significa "último día del mes" (igual que en la API). */
export const LAST_DAY_OF_MONTH = 31;

export type PeriodOpsConfig = {
  periodoCierreEnabled: boolean;
  periodoCierreDia: number;
  periodoCierreHora: number;
  periodoCierreMinuto: number;
  periodoCierreLastRunAt: string | null;
  desactivarImpagasEnabled: boolean;
  desactivarImpagasDia: number;
  desactivarImpagasHora: number;
  desactivarImpagasMinuto: number;
  desactivarImpagasNotifyWhatsApp: boolean;
  desactivarImpagasLastRunAt: string | null;
  ventanaPagoDiaCierre: number;
  autoConfirmarDisponible: boolean;
  autoConfirmarEnabled: boolean;
  autoConfirmarDia: number;
  autoConfirmarHora: number;
  autoConfirmarMinuto: number;
  autoConfirmarLastRunAt: string | null;
  timezone: string;
  updatedAt: string | null;
};

export const DEFAULT_PERIOD_OPS_CONFIG: PeriodOpsConfig = {
  periodoCierreEnabled: true,
  periodoCierreDia: 1,
  periodoCierreHora: 8,
  periodoCierreMinuto: 0,
  periodoCierreLastRunAt: null,
  desactivarImpagasEnabled: true,
  desactivarImpagasDia: 11,
  desactivarImpagasHora: 9,
  desactivarImpagasMinuto: 0,
  desactivarImpagasNotifyWhatsApp: true,
  desactivarImpagasLastRunAt: null,
  ventanaPagoDiaCierre: 10,
  autoConfirmarDisponible: false,
  autoConfirmarEnabled: true,
  autoConfirmarDia: 4,
  autoConfirmarHora: 9,
  autoConfirmarMinuto: 0,
  autoConfirmarLastRunAt: null,
  timezone: 'America/Argentina/Buenos_Aires',
  updatedAt: null,
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function periodOpsTimeValue(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseTime(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(':').map(Number);
  return {
    hour: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0,
    minute: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0,
  };
}

const fieldControlClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const fieldLabelClass = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

function SectionCard({
  title,
  description,
  children,
  tone = 'default',
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  tone?: 'default' | 'danger';
}) {
  const frame =
    tone === 'danger' ? 'border-rose-200 dark:border-rose-900/50' : '';
  return (
    <section className={`panel-card p-5 sm:p-6 ${frame}`}>
      <header className="mb-5">
        <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
          {title}
        </h2>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function SwitchRow({
  title,
  description,
  enabled,
  onLabel,
  offLabel,
  disabled,
  onToggle,
  plain = false,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onLabel: string;
  offLabel: string;
  disabled: boolean;
  onToggle: () => void;
  plain?: boolean;
}) {
  return (
    <div
      className={
        plain
          ? 'flex items-start justify-between gap-4'
          : 'flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 dark:border-gray-700 dark:bg-gray-900/50'
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
              enabled
                ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                : 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300'
            }`}
          >
            {enabled ? onLabel : offLabel}
          </span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={onToggle}
        className={`relative mt-0.5 inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 ${
          enabled ? 'bg-teal-600' : 'bg-slate-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
            enabled ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function DayTimeFields({
  dayLabel,
  timeLabel,
  day,
  hour,
  minute,
  dayOptions,
  disabled,
  onDayChange,
  onTimeChange,
  formatDay,
}: {
  dayLabel: string;
  timeLabel: string;
  day: number;
  hour: number;
  minute: number;
  dayOptions: number[];
  disabled: boolean;
  onDayChange: (day: number) => void;
  onTimeChange: (hour: number, minute: number) => void;
  /** Texto de cada opción de día (ej. 31 → "Último día del mes"). */
  formatDay?: (day: number) => string;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="w-full">
        <label className={fieldLabelClass}>{dayLabel}</label>
        <select
          disabled={disabled}
          className={fieldControlClass}
          value={day}
          onChange={(e) => onDayChange(Number(e.target.value))}
        >
          {dayOptions.map((d) => (
            <option key={d} value={d}>
              {formatDay ? formatDay(d) : d}
            </option>
          ))}
        </select>
      </div>
      <Input
        label={timeLabel}
        type="time"
        disabled={disabled}
        value={periodOpsTimeValue(hour, minute)}
        onChange={(e) => {
          const parsed = parseTime(e.target.value);
          onTimeChange(parsed.hour, parsed.minute);
        }}
      />
    </div>
  );
}

type PeriodJobKey = 'periodo' | 'autoconfirmar' | 'desactivar_impagas';

type JobRun = {
  id: number;
  periodo: string;
  origen: 'auto' | 'manual';
  usuario: string | null;
  iniciadoAt: string;
  finalizadoAt: string | null;
  estado: 'en_curso' | 'ok' | 'error';
  resumen: string | null;
  error: string | null;
};

type JobStatus = {
  key: PeriodJobKey;
  enabled: boolean;
  day: number;
  hour: number;
  minute: number;
  periodo: string;
  state: 'hecho' | 'programado' | 'por_correr' | 'atrasado' | 'desactivado';
  lastRunAt: string | null;
  recentRuns: JobRun[];
  unavailable?: boolean;
};

const STATE_BADGE: Record<JobStatus['state'], string> = {
  hecho: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  programado: 'bg-slate-200 text-slate-700 dark:bg-gray-700 dark:text-slate-200',
  por_correr: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  atrasado: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  desactivado: 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300',
};

/** Estado del mes, "Correr ahora" e historial de corridas de un proceso del período. */
function JobRunPanel({
  status,
  canRun,
  running,
  onRun,
}: {
  status: JobStatus | undefined;
  canRun: boolean;
  running: boolean;
  onRun: () => void;
}) {
  const { t } = useTranslation();
  const [showHistory, setShowHistory] = useState(false);
  if (!status) return null;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
  const when = `${status.day === LAST_DAY_OF_MONTH ? t('pages.periodOps.lastDayOfMonth') : status.day} ${periodOpsTimeValue(status.hour, status.minute)}`;
  const lastRun = status.recentRuns[0];

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('pages.periodOps.jobThisMonth', { periodo: status.periodo })}
            </p>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATE_BADGE[status.state]}`}>
              {t(`pages.periodOps.jobState.${status.state}`)}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {status.state === 'hecho'
              ? t('pages.periodOps.jobDoneAt', { at: fmt(status.lastRunAt) })
              : status.state === 'desactivado'
                ? t('pages.periodOps.jobDisabledHint')
                : status.state === 'atrasado'
                  ? t('pages.periodOps.jobLateHint', { when })
                  : status.state === 'por_correr'
                    ? t('pages.periodOps.jobDueHint')
                    : t('pages.periodOps.jobScheduledHint', { when })}
          </p>
          {lastRun?.estado === 'error' ? (
            <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
              {t('pages.periodOps.jobLastError', { at: fmt(lastRun.iniciadoAt), error: lastRun.error ?? '' })}
            </p>
          ) : null}
        </div>
        {canRun ? (
          <Button type="button" size="sm" variant="secondary" onClick={onRun} disabled={running || status.unavailable}>
            {running ? t('pages.periodOps.jobRunning') : t('pages.periodOps.jobRunNow')}
          </Button>
        ) : null}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="text-xs font-medium text-teal-700 hover:underline dark:text-teal-400"
        >
          {showHistory ? t('pages.periodOps.jobHideHistory') : t('pages.periodOps.jobShowHistory', { count: status.recentRuns.length })}
        </button>
        {showHistory ? (
          status.recentRuns.length === 0 ? (
            <p className="mt-2 text-xs text-slate-500">{t('pages.periodOps.jobNoRuns')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100 text-xs dark:divide-gray-700">
              {status.recentRuns.map((r) => (
                <li key={r.id} className="py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="tabular-nums text-slate-600 dark:text-slate-300">{fmt(r.iniciadoAt)}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600 dark:bg-gray-700 dark:text-slate-300">
                      {r.origen === 'auto' ? t('pages.periodOps.jobOriginAuto') : t('pages.periodOps.jobOriginManual')}
                    </span>
                    {r.origen === 'manual' && r.usuario ? <span className="text-slate-500">{r.usuario}</span> : null}
                    <span
                      className={
                        r.estado === 'ok'
                          ? 'font-semibold text-emerald-700 dark:text-emerald-300'
                          : r.estado === 'error'
                            ? 'font-semibold text-rose-700 dark:text-rose-300'
                            : 'font-semibold text-sky-700 dark:text-sky-300'
                      }
                    >
                      {t(`pages.periodOps.jobRunState.${r.estado}`)}
                    </span>
                  </div>
                  {r.resumen ? <p className="mt-0.5 text-slate-500 dark:text-slate-400">{r.resumen}</p> : null}
                  {r.error ? <p className="mt-0.5 text-rose-700 dark:text-rose-300">{r.error}</p> : null}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </div>
  );
}

/** `extra`: secciones que van al final de la zona con scroll (ej. recargos automáticos), con su propio guardado. */
export const PeriodOpsConfigForm: React.FC<{ canEdit: boolean; extra?: React.ReactNode }> = ({ canEdit, extra }) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [config, setConfig] = useState<PeriodOpsConfig>(DEFAULT_PERIOD_OPS_CONFIG);
  const [draft, setDraft] = useState<PeriodOpsConfig>(DEFAULT_PERIOD_OPS_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [jobs, setJobs] = useState<Partial<Record<PeriodJobKey, JobStatus>>>({});
  const [runningJob, setRunningJob] = useState<PeriodJobKey | null>(null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await httpClient.get<JobStatus[]>('/panel-config/jobs');
      const map: Partial<Record<PeriodJobKey, JobStatus>> = {};
      for (const j of res.data ?? []) map[j.key] = j;
      setJobs(map);
    } catch {
      // sin estado de jobs: el resto de la configuración sigue funcionando
    }
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<PeriodOpsConfig>('/panel-config');
      const next = { ...DEFAULT_PERIOD_OPS_CONFIG, ...res.data };
      setConfig(next);
      setDraft(next);
    } catch {
      showError(t('pages.periodOps.configLoadError'));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    void load();
    void loadJobs();
  }, [load, loadJobs]);

  const runJob = async (key: PeriodJobKey) => {
    const confirmKey =
      key === 'periodo'
        ? 'pages.periodOps.runMonthlyConfirm'
        : key === 'autoconfirmar'
          ? 'pages.periodOps.jobConfirmAutoConfirm'
          : draft.desactivarImpagasNotifyWhatsApp
            ? 'pages.periodOps.deactivateClinicsConfirm'
            : 'pages.periodOps.deactivateClinicsConfirmNoWa';
    if (!window.confirm(t(confirmKey))) return;
    setRunningJob(key);
    try {
      const res = await httpClient.post<{ resumen: string; marked: boolean }>(`/panel-config/jobs/${key}/run`);
      success(
        `${res.data.resumen}${res.data.marked ? '' : ` ${t('pages.periodOps.jobNotMarked')}`}`
      );
    } catch (err) {
      const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      showError(typeof message === 'string' && message ? message : t('pages.periodOps.jobRunError'));
    } finally {
      setRunningJob(null);
      void loadJobs();
      void load();
    }
  };

  const dirty = useMemo(() => {
    return (
      draft.periodoCierreEnabled !== config.periodoCierreEnabled ||
      draft.periodoCierreDia !== config.periodoCierreDia ||
      draft.periodoCierreHora !== config.periodoCierreHora ||
      draft.periodoCierreMinuto !== config.periodoCierreMinuto ||
      draft.desactivarImpagasEnabled !== config.desactivarImpagasEnabled ||
      draft.desactivarImpagasDia !== config.desactivarImpagasDia ||
      draft.desactivarImpagasHora !== config.desactivarImpagasHora ||
      draft.desactivarImpagasMinuto !== config.desactivarImpagasMinuto ||
      draft.desactivarImpagasNotifyWhatsApp !== config.desactivarImpagasNotifyWhatsApp ||
      draft.ventanaPagoDiaCierre !== config.ventanaPagoDiaCierre ||
      draft.autoConfirmarEnabled !== config.autoConfirmarEnabled ||
      draft.autoConfirmarDia !== config.autoConfirmarDia ||
      draft.autoConfirmarHora !== config.autoConfirmarHora ||
      draft.autoConfirmarMinuto !== config.autoConfirmarMinuto
    );
  }, [config, draft]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setIsSaving(true);
    try {
      const res = await httpClient.put<PeriodOpsConfig>('/panel-config', {
        periodoCierreEnabled: draft.periodoCierreEnabled,
        periodoCierreDia: draft.periodoCierreDia,
        periodoCierreHora: draft.periodoCierreHora,
        periodoCierreMinuto: draft.periodoCierreMinuto,
        desactivarImpagasEnabled: draft.desactivarImpagasEnabled,
        desactivarImpagasDia: draft.desactivarImpagasDia,
        desactivarImpagasHora: draft.desactivarImpagasHora,
        desactivarImpagasMinuto: draft.desactivarImpagasMinuto,
        desactivarImpagasNotifyWhatsApp: draft.desactivarImpagasNotifyWhatsApp,
        ventanaPagoDiaCierre: draft.ventanaPagoDiaCierre,
        ...(config.autoConfirmarDisponible
          ? {
              autoConfirmarEnabled: draft.autoConfirmarEnabled,
              autoConfirmarDia: draft.autoConfirmarDia,
              autoConfirmarHora: draft.autoConfirmarHora,
              autoConfirmarMinuto: draft.autoConfirmarMinuto,
            }
          : {}),
      });
      const next = { ...DEFAULT_PERIOD_OPS_CONFIG, ...res.data };
      setConfig(next);
      setDraft(next);
      success(t('pages.periodOps.configSaved'));
    } catch {
      showError(t('pages.periodOps.configSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatLastRun = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : t('pages.periodOps.neverRun');

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);
  // El corte puede ser además el último día del mes (31 = 28/29/30/31 según el mes).
  const cutDayOptions = [...dayOptions, LAST_DAY_OF_MONTH];
  const formatCutDay = (d: number) => (d === LAST_DAY_OF_MONTH ? t('pages.periodOps.lastDayOfMonth') : String(d));
  const graceWarn = draft.desactivarImpagasDia < draft.ventanaPagoDiaCierre;
  // Confirmar después del vencimiento deja las facturas vencidas apenas se confirman.
  const autoConfirmLate = draft.autoConfirmarDia >= draft.ventanaPagoDiaCierre;
  const fieldsDisabled = isSaving || !canEdit;

  return (
    <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : (
        <>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-4">
            <SectionCard
              title={t('pages.periodOps.paymentWindowTitle')}
              description={t('pages.periodOps.paymentWindowHint')}
            >
              <div className="max-w-[12rem]">
                <Input
                  label={t('pages.periodOps.paymentWindowDay')}
                  type="number"
                  min={1}
                  max={28}
                  disabled={fieldsDisabled}
                  value={draft.ventanaPagoDiaCierre}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      ventanaPagoDiaCierre: Math.min(28, Math.max(1, Number(e.target.value) || 1)),
                    }))
                  }
                />
              </div>
            </SectionCard>

            <SectionCard
              title={t('pages.periodOps.closeAutoTitle')}
              description={t('pages.periodOps.closeAutoHint')}
            >
              <SwitchRow
                title={t('pages.periodOps.enableToggle')}
                description={t('pages.periodOps.timezoneNote', { tz: draft.timezone })}
                enabled={draft.periodoCierreEnabled}
                onLabel={t('pages.periodOps.autoOn')}
                offLabel={t('pages.periodOps.autoOff')}
                disabled={fieldsDisabled}
                onToggle={() =>
                  setDraft((prev) => ({
                    ...prev,
                    periodoCierreEnabled: !prev.periodoCierreEnabled,
                  }))
                }
              />
              <JobRunPanel
                status={jobs.periodo}
                canRun={canEdit}
                running={runningJob === 'periodo'}
                onRun={() => void runJob('periodo')}
              />
              {draft.periodoCierreEnabled ? (
                <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('pages.periodOps.closeScheduleTitle')}
                  </p>
                  <DayTimeFields
                    dayLabel={t('pages.periodOps.dayOfMonth')}
                    timeLabel={t('pages.periodOps.time')}
                    day={draft.periodoCierreDia}
                    hour={draft.periodoCierreHora}
                    minute={draft.periodoCierreMinuto}
                    dayOptions={dayOptions}
                    disabled={fieldsDisabled}
                    onDayChange={(periodoCierreDia) =>
                      setDraft((prev) => ({ ...prev, periodoCierreDia }))
                    }
                    onTimeChange={(periodoCierreHora, periodoCierreMinuto) =>
                      setDraft((prev) => ({ ...prev, periodoCierreHora, periodoCierreMinuto }))
                    }
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('pages.periodOps.lastAutoRun')}: {formatLastRun(config.periodoCierreLastRunAt)}
                  </p>
                </div>
              ) : null}
            </SectionCard>

            <SectionCard
              title={t('pages.periodOps.autoConfirmTitle')}
              description={t('pages.periodOps.autoConfirmHint')}
            >
              <JobRunPanel
                status={jobs.autoconfirmar}
                canRun={canEdit}
                running={runningJob === 'autoconfirmar'}
                onRun={() => void runJob('autoconfirmar')}
              />
              {config.autoConfirmarDisponible ? (
                <>
                  <SwitchRow
                    title={t('pages.periodOps.enableToggle')}
                    description={t('pages.periodOps.timezoneNote', { tz: draft.timezone })}
                    enabled={draft.autoConfirmarEnabled}
                    onLabel={t('pages.periodOps.autoOn')}
                    offLabel={t('pages.periodOps.autoOff')}
                    disabled={fieldsDisabled}
                    onToggle={() =>
                      setDraft((prev) => ({ ...prev, autoConfirmarEnabled: !prev.autoConfirmarEnabled }))
                    }
                  />
                  {draft.autoConfirmarEnabled ? (
                    <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        {t('pages.periodOps.autoConfirmScheduleTitle')}
                      </p>
                      <DayTimeFields
                        dayLabel={t('pages.periodOps.dayOfMonth')}
                        timeLabel={t('pages.periodOps.time')}
                        day={draft.autoConfirmarDia}
                        hour={draft.autoConfirmarHora}
                        minute={draft.autoConfirmarMinuto}
                        dayOptions={dayOptions}
                        disabled={fieldsDisabled}
                        onDayChange={(autoConfirmarDia) => setDraft((prev) => ({ ...prev, autoConfirmarDia }))}
                        onTimeChange={(autoConfirmarHora, autoConfirmarMinuto) =>
                          setDraft((prev) => ({ ...prev, autoConfirmarHora, autoConfirmarMinuto }))
                        }
                      />
                      {autoConfirmLate ? (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          {t('pages.periodOps.autoConfirmLateWarn')}
                        </p>
                      ) : null}
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('pages.periodOps.lastAutoRun')}: {formatLastRun(config.autoConfirmarLastRunAt)}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {t('pages.periodOps.autoConfirmMissingMigration')}
                </p>
              )}
            </SectionCard>

            <SectionCard
              title={t('pages.periodOps.deactivateAutoTitle')}
              description={t('pages.periodOps.deactivateAutoHint')}
              tone="danger"
            >
              <SwitchRow
                title={t('pages.periodOps.enableToggle')}
                description={
                  draft.desactivarImpagasEnabled
                    ? t('pages.periodOps.deactivateAutoOn')
                    : t('pages.periodOps.deactivateAutoOffNote')
                }
                enabled={draft.desactivarImpagasEnabled}
                onLabel={t('pages.periodOps.autoOn')}
                offLabel={t('pages.periodOps.autoOff')}
                disabled={fieldsDisabled}
                onToggle={() =>
                  setDraft((prev) => ({
                    ...prev,
                    desactivarImpagasEnabled: !prev.desactivarImpagasEnabled,
                  }))
                }
              />
              <JobRunPanel
                status={jobs.desactivar_impagas}
                canRun={canEdit}
                running={runningJob === 'desactivar_impagas'}
                onRun={() => void runJob('desactivar_impagas')}
              />
              {draft.desactivarImpagasEnabled ? (
                <div className="space-y-4 rounded-xl border border-rose-100 bg-rose-50/50 p-4 dark:border-rose-900/40 dark:bg-rose-950/20">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('pages.periodOps.deactivateScheduleTitle')}
                  </p>
                  <DayTimeFields
                    dayLabel={t('pages.periodOps.dayOfMonth')}
                    timeLabel={t('pages.periodOps.time')}
                    day={draft.desactivarImpagasDia}
                    hour={draft.desactivarImpagasHora}
                    minute={draft.desactivarImpagasMinuto}
                    dayOptions={cutDayOptions}
                    formatDay={formatCutDay}
                    disabled={fieldsDisabled}
                    onDayChange={(desactivarImpagasDia) =>
                      setDraft((prev) => ({ ...prev, desactivarImpagasDia }))
                    }
                    onTimeChange={(desactivarImpagasHora, desactivarImpagasMinuto) =>
                      setDraft((prev) => ({
                        ...prev,
                        desactivarImpagasHora,
                        desactivarImpagasMinuto,
                      }))
                    }
                  />
                  {draft.desactivarImpagasDia === LAST_DAY_OF_MONTH ? (
                    <p className="text-xs text-slate-600 dark:text-slate-300">{t('pages.periodOps.lastDayCutHint')}</p>
                  ) : null}
                  <SwitchRow
                    title={t('pages.periodOps.notifyWhatsAppTitle')}
                    description={t('pages.periodOps.notifyWhatsAppHint')}
                    enabled={draft.desactivarImpagasNotifyWhatsApp}
                    onLabel={t('pages.periodOps.notifyOn')}
                    offLabel={t('pages.periodOps.notifyOff')}
                    plain
                    disabled={fieldsDisabled}
                    onToggle={() =>
                      setDraft((prev) => ({
                        ...prev,
                        desactivarImpagasNotifyWhatsApp: !prev.desactivarImpagasNotifyWhatsApp,
                      }))
                    }
                  />
                  {graceWarn ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t('pages.periodOps.graceWarn')}
                    </p>
                  ) : null}
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('pages.periodOps.lastAutoRun')}:{' '}
                    {formatLastRun(config.desactivarImpagasLastRunAt)}
                  </p>
                </div>
              ) : null}
            </SectionCard>
            {extra}
          </div>

          {canEdit ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-1 py-4 dark:border-gray-700 dark:bg-transparent">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {dirty ? t('pages.periodOps.unsavedChanges') : t('pages.periodOps.timezoneNote', { tz: draft.timezone })}
              </p>
              <Button type="submit" variant="primary" size="sm" disabled={!dirty || isSaving}>
                {isSaving ? t('common.saving') : t('pages.periodOps.configSave')}
              </Button>
            </div>
          ) : (
            <p className="pt-2 text-xs text-gray-500">{t('pages.settings.readOnly')}</p>
          )}
        </>
      )}
    </form>
  );
};
